import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { ChevronDown, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const logoImage = new URL('../assets/logo-ph.svg', import.meta.url).href;

export function Header() {
  const [whoWeServeOpen, setWhoWeServeOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileWhoWeServeOpen, setMobileWhoWeServeOpen] = useState(false);

  const whoWeServeRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  type TimerRef = React.RefObject<ReturnType<typeof setTimeout> | null>;
  const open = (set: (v: boolean) => void, ref: TimerRef) => {
    if (ref.current) clearTimeout(ref.current);
    set(true);
  };
  const closeDelayed = (set: (v: boolean) => void, ref: TimerRef) => {
    if (ref.current) clearTimeout(ref.current);
    ref.current = setTimeout(() => { set(false); ref.current = null; }, 200);
  };

  return (
    <header className="bg-white border-b border-[#4B5563]/10 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          {/* Logo */}
          <div className="flex items-center flex-shrink-0">
            <Link to="/">
              <img src={logoImage} alt="PleromaHealth" className="h-11" />
            </Link>
          </div>

          {/* Desktop nav + CTA — right-aligned, same pattern as original */}
          <div className="hidden lg:flex items-center gap-6 ml-auto">
            <nav className="flex items-center gap-6">

              <Link to="/healthbridge"
                className="text-[#4B5563] hover:text-[#34AFFB] transition-colors text-m font-normal whitespace-nowrap">
                HealthBridge
              </Link>

              {/* Solutions dropdown */}
              <div className="relative"
                onMouseEnter={() => open(setWhoWeServeOpen, whoWeServeRef)}
                onMouseLeave={() => closeDelayed(setWhoWeServeOpen, whoWeServeRef)}>
                <button className="text-[#4B5563] hover:text-[#34AFFB] transition-colors flex items-center gap-1 text-m font-normal whitespace-nowrap">
                  Solutions
                  <ChevronDown className="h-4 w-4" />
                </button>
                {whoWeServeOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-[#4B5563]/10 py-2 z-50"
                    onMouseEnter={() => open(setWhoWeServeOpen, whoWeServeRef)}
                    onMouseLeave={() => closeDelayed(setWhoWeServeOpen, whoWeServeRef)}>
                    <Link to="/who-we-serve/providers-and-acos"
                      className="block px-4 py-3 text-[#4B5563] hover:bg-[#34AFFB]/10 hover:text-[#34AFFB] transition-colors"
                      onClick={() => setWhoWeServeOpen(false)}>
                      <div className="font-medium">Providers &amp; ACOs</div>
                      <div className="text-sm text-[#4B5563]/70">Foresight and friction-free workflows</div>
                    </Link>
                    <Link to="/who-we-serve/payers"
                      className="block px-4 py-3 text-[#4B5563] hover:bg-[#34AFFB]/10 hover:text-[#34AFFB] transition-colors"
                      onClick={() => setWhoWeServeOpen(false)}>
                      <div className="font-medium">Payers &amp; Health Plans</div>
                      <div className="text-sm text-[#4B5563]/70">Risk, Stars, and quality intelligence</div>
                    </Link>
                  </div>
                )}
              </div>

              <Link to="/about"
                className="text-[#4B5563] hover:text-[#34AFFB] transition-colors text-m font-normal whitespace-nowrap">
                About Us
              </Link>

              <Link to="/insights"
                className="text-[#4B5563] hover:text-[#34AFFB] transition-colors text-m font-normal whitespace-nowrap">
                Insights
              </Link>

            </nav>

            <Button asChild className="bg-[#34AFFB] hover:bg-[#34AFFB]/90 text-white whitespace-nowrap">
              <Link to="/#contact">Book a demo</Link>
            </Button>
          </div>

          {/* Hamburger — below lg */}
          <div className="lg:hidden">
            <button
              className="p-2 text-[#4B5563] hover:text-[#34AFFB]"
              aria-label="Open menu"
              onClick={() => setMobileMenuOpen(true)}
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            {/* Full-screen overlay — portaled to body so sticky header can't clip it */}
            {mobileMenuOpen && typeof document !== 'undefined' && createPortal(<div className="fixed inset-0 z-[100] flex justify-end overflow-hidden">
                {/* Backdrop */}
                <div
                  className="absolute inset-0 bg-black/50"
                  onClick={() => setMobileMenuOpen(false)}
                />

                {/* Drawer panel — h-screen + min-h-0 on nav prevents flex overflow past viewport */}
                <div className="relative z-[101] w-4/5 max-w-[320px] bg-white shadow-2xl flex flex-col" style={{ height: '100dvh' }}>

                  {/* Drawer header */}
                  <div className="flex items-center justify-between px-6 py-6 border-b border-gray-100">
                    <Link to="/" onClick={() => setMobileMenuOpen(false)}>
                      <img src={logoImage} alt="PleromaHealth" className="h-8" />
                    </Link>
                    <button
                      onClick={() => setMobileMenuOpen(false)}
                      className="p-2 rounded-full text-gray-500 hover:text-[#34AFFB] hover:bg-gray-100 transition-colors"
                      aria-label="Close menu"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Nav — min-h-0 lets flex-1 shrink so it scrolls instead of expanding the drawer */}
                  <nav className="flex-1 overflow-y-auto min-h-0">

                    <Link
                      to="/healthbridge"
                      className="flex items-center px-6 py-5 text-sm font-normal text-gray-800 hover:text-[#34AFFB] hover:bg-gray-50 transition-colors border-b border-gray-100"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      HealthBridge
                    </Link>

                    {/* Solutions accordion */}
                    <div className="border-b border-gray-100">
                      <button
                        className="w-full flex items-center justify-between px-6 py-5 text-sm font-normal text-gray-800 hover:bg-gray-50 transition-colors"
                        onClick={() => setMobileWhoWeServeOpen(v => !v)}
                      >
                        <span className={mobileWhoWeServeOpen ? 'text-[#34AFFB]' : ''}>Solutions</span>
                        <ChevronDown
                          className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${mobileWhoWeServeOpen ? 'rotate-180 text-[#34AFFB]' : 'text-gray-400'}`}
                        />
                      </button>
                      {mobileWhoWeServeOpen && (
                        <div style={{ backgroundColor: '#EFF6FF', borderTop: '1px solid #BFDBFE' }}>
                          <Link
                            to="/who-we-serve/providers-and-acos"
                            style={{ borderLeft: '3px solid #34AFFB', display: 'block', padding: '18px 24px 18px 24px', borderBottom: '1px solid #DBEAFE' }}
                            className="hover:bg-blue-100 transition-colors"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <span style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#1F2937' }}>Providers &amp; ACOs</span>
                            <span style={{ display: 'block', fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>Foresight and friction-free workflows</span>
                          </Link>
                          <Link
                            to="/who-we-serve/payers"
                            style={{ borderLeft: '3px solid #34AFFB', display: 'block', padding: '18px 24px' }}
                            className="hover:bg-blue-100 transition-colors"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <span style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#1F2937' }}>Payers &amp; Health Plans</span>
                            <span style={{ display: 'block', fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>Risk, Stars, and quality intelligence</span>
                          </Link>
                        </div>
                      )}
                    </div>

                    <Link
                      to="/about"
                      className="flex items-center px-6 py-5 text-sm font-normal text-gray-800 hover:text-[#34AFFB] hover:bg-gray-50 transition-colors border-b border-gray-100"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      About Us
                    </Link>

                    <Link
                      to="/insights"
                      className="flex items-center px-6 py-5 text-sm font-normal text-gray-800 hover:text-[#34AFFB] hover:bg-gray-50 transition-colors border-b border-gray-100"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Insights
                    </Link>

                  </nav>

                  {/* Pinned CTA */}
                  <div className="px-5 py-5 border-t border-gray-100 bg-white">
                    <Button asChild className="w-full bg-[#34AFFB] hover:bg-[#1E88E5] text-white font-semibold h-9 text-sm rounded-xl">
                      <Link to="/#contact" onClick={() => setMobileMenuOpen(false)}>Book a demo</Link>
                    </Button>
                  </div>

                </div>
              </div>, document.body)}
          </div>

        </div>
      </div>
    </header>
  );
}
